// src/components/ProfileTaglineControl.js
//
// Account → the optional one-liner shown in a member's card (PHOTO-002).
// Edits INLINE in the profile card — deliberately no overlay, so nothing here
// can be trapped or clipped by the card's backdrop-filter/overflow.
//
//   empty → "Add a line about you" (quiet primary text button)
//   set   → the tagline in soft italic; tap it to edit
//   edit  → one field (40 chars, live counter, rotating example placeholder),
//           "Visible to other members", Save / Cancel; Enter saves, Esc cancels.
//           Saving an empty field removes the tagline.

import React, { useEffect, useRef, useState } from 'react';
import { fetchOwnMemberProfile, saveMemberTagline, cleanTagline, TAGLINE_MAX } from '../services/memberProfiles';

const DEFAULT_EXAMPLES = ['45 · Austin · mom of three', 'Loves soccer and early classes', '66 · UK · new to lifting'];

export function taglineCopy(c = {}) {
    const pick = (key, fallback) => (typeof c[key] === 'string' && c[key].trim() ? c[key] : fallback);
    const examplesRaw = pick('profileTaglineExamples', '');
    const examples = examplesRaw
        ? examplesRaw.split('|').map((x) => x.trim()).filter(Boolean)
        : DEFAULT_EXAMPLES;
    return {
        addLabel: pick('profileTaglineAddLabel', 'Add a line about you'),
        privacyNote: pick('profileTaglinePrivacyNote', 'Visible to other members'),
        savedLabel: pick('profileTaglineSavedLabel', 'Saved.'),
        errorSave: pick('profileTaglineErrorSave', 'Couldn’t save that — try again.'),
        examples: examples.length ? examples : DEFAULT_EXAMPLES,
    };
}

function ProfileTaglineControl({ userId, content }) {
    const copy = taglineCopy(content);
    const [tagline, setTagline] = useState('');
    const [loaded, setLoaded] = useState(false);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [savedFlash, setSavedFlash] = useState(false);
    const [hover, setHover] = useState(false);
    const [exampleIdx, setExampleIdx] = useState(0);
    const inputRef = useRef(null);
    const triggerRef = useRef(null);
    const flashTimer = useRef(null);

    useEffect(() => {
        if (!userId) return undefined;
        let cancelled = false;
        fetchOwnMemberProfile(userId).then((p) => {
            if (cancelled) return;
            setTagline(p.tagline || ''); setLoaded(true);
        });
        return () => { cancelled = true; };
    }, [userId]);

    // Rotate the example placeholder while the empty field is open.
    useEffect(() => {
        if (!editing || draft) return undefined;
        const t = setInterval(() => setExampleIdx((i) => (i + 1) % copy.examples.length), 3200);
        return () => clearInterval(t);
    }, [editing, draft, copy.examples.length]);

    useEffect(() => () => clearTimeout(flashTimer.current), []);

    const startEdit = () => {
        setDraft(tagline); setError(''); setEditing(true);
        setTimeout(() => inputRef.current?.focus(), 0);
    };
    const cancel = () => {
        setEditing(false); setError('');
        setTimeout(() => triggerRef.current?.focus(), 0);
    };
    const save = async () => {
        if (saving) return;
        const next = cleanTagline(draft);
        if (next === tagline) { cancel(); return; }
        setSaving(true); setError('');
        try {
            const stored = await saveMemberTagline(userId, next);
            setTagline(stored);
            setEditing(false);
            setSavedFlash(true);
            clearTimeout(flashTimer.current);
            flashTimer.current = setTimeout(() => setSavedFlash(false), 2500);
            setTimeout(() => triggerRef.current?.focus(), 0);
        } catch (e) {
            setError(copy.errorSave);
        }
        setSaving(false);
    };

    if (!userId || !loaded) return <div style={{ minHeight: 8 }} />;

    const soft = 'color-mix(in srgb, var(--mom-charcoal) 70%, var(--mom-cream))';
    const remaining = TAGLINE_MAX - draft.length;

    if (editing) {
        return (
            <div style={{ maxWidth: 320, margin: '2px auto 14px', textAlign: 'left', animation: 'fadeIn 0.18s ease-out' }}>
                <div style={{ position: 'relative' }}>
                    <input
                        ref={inputRef}
                        type="text"
                        className="mom-input"
                        value={draft}
                        maxLength={TAGLINE_MAX}
                        aria-label="A line about you"
                        aria-describedby="mom-tagline-note"
                        placeholder={copy.examples[exampleIdx % copy.examples.length]}
                        onChange={(e) => { setDraft(e.target.value.slice(0, TAGLINE_MAX)); setError(''); }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); save(); }
                            if (e.key === 'Escape') { e.preventDefault(); cancel(); }
                        }}
                        style={{ fontSize: 14, textAlign: 'center', paddingRight: 44 }}
                    />
                    <span aria-hidden="true" style={{
                        position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                        fontFamily: 'var(--mom-font-sans)', fontSize: 11.5, fontVariantNumeric: 'tabular-nums',
                        color: remaining <= 5 ? 'var(--mom-primary)' : soft,
                    }}>{remaining}</span>
                </div>
                <p id="mom-tagline-note" style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                    fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: soft, margin: '8px 0 10px',
                }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <circle cx="9" cy="8" r="3.5" /><path d="M2.5 20c.8-3.4 3.4-5.5 6.5-5.5s5.7 2.1 6.5 5.5" /><circle cx="17" cy="9" r="2.5" /><path d="M16 14.6c2.6 0 4.6 1.8 5.3 4.4" />
                    </svg>
                    {copy.privacyNote}
                </p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                    <button className="mom-btn mom-btn-primary" disabled={saving} onClick={save}
                        style={{ padding: '8px 22px', fontSize: 13, borderRadius: 50, opacity: saving ? 0.6 : 1 }}>
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button className="mom-btn mom-btn-ghost" disabled={saving} onClick={cancel}
                        style={{ fontSize: 13, color: 'var(--mom-charcoal)' }}>Cancel</button>
                </div>
                {error && (
                    <p role="alert" style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, color: '#a3263f', textAlign: 'center', margin: '8px 0 0' }}>{error}</p>
                )}
            </div>
        );
    }

    return (
        <div style={{ margin: '0 auto 10px' }}>
            {tagline ? (
                <button
                    ref={triggerRef} type="button" onClick={startEdit}
                    aria-label={`Your line: ${tagline}. Edit`}
                    onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%',
                        padding: '4px 10px', minHeight: 32, border: 'none', borderRadius: 50, cursor: 'pointer',
                        background: hover ? 'color-mix(in srgb, var(--mom-charcoal) 6%, transparent)' : 'transparent',
                        transition: 'background 0.15s ease',
                        fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 15, color: soft,
                    }}
                >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tagline}</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                        aria-hidden="true" style={{ flexShrink: 0, opacity: hover ? 0.9 : 0.45, transition: 'opacity 0.15s ease' }}>
                        <path d="M4 20h4L19 9l-4-4L4 16v4z" />
                    </svg>
                </button>
            ) : (
                <button
                    ref={triggerRef} type="button" onClick={startEdit}
                    style={{
                        padding: '4px 12px', minHeight: 32, border: 'none', background: 'transparent', cursor: 'pointer',
                        fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: 'var(--mom-primary)',
                    }}
                >{copy.addLabel}</button>
            )}
            <div role="status" aria-live="polite" style={{
                fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, fontStyle: 'italic', color: soft,
                minHeight: savedFlash ? 16 : 0,
            }}>{savedFlash ? copy.savedLabel : ''}</div>
        </div>
    );
}

export default ProfileTaglineControl;
